import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import type { ChangeEvent } from 'react';
import { useState } from 'react';

import { ClickableTableRow } from '@/components/management/table/ClickableTableRow';
import { useAbility } from '@/hooks/useAbility';
import { useUserSuspenseQuery } from '@/hooks/useUserSuspenseQuery';
import type { ParkAndRideFacility } from '@/modules/api/backend';
import { Action, Subject } from '@/modules/api/backend';

interface ParkAndRideFacilitiesTableProps {
  facilities: ParkAndRideFacility[];
  setRef?: (idx: number) => (ref: HTMLDivElement | null) => void;
  title?: string;
  stationId?: string;
}

export function ParkAndRideFacilitiesTable({ facilities, setRef, title, stationId }: ParkAndRideFacilitiesTableProps) {
  const { ability } = useAbility();
  const { data: user } = useUserSuspenseQuery();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const canAccess = (orgId?: string) =>
    !!ability && (ability.can(Action.MANAGE, Subject.All) || orgId === user.organization?.organizationId);

  return (
    <Stack direction="column" pt={6} spacing={2}>
      {title ? (
        <Typography {...(setRef && { ref: setRef(2) })} variant="h2">
          {title}
        </Typography>
      ) : null}
      <TableContainer>
        <Table aria-label="P+R Anlagen">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Sichtbarkeit</TableCell>
              <TableCell>Zuletzt geändert</TableCell>
              <TableCell>Organisation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facilities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((facility) => {
              const clickable = canAccess(facility.organization?.id);
              const handleClick = async () =>
                navigate(
                  stationId
                    ? {
                        to: '/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId',
                        params: { parkAndRideFacilityId: facility.id, stationId },
                      }
                    : {
                        to: '/management/stationless/$parkAndRideFacilityId',
                        params: { parkAndRideFacilityId: facility.id },
                      },
                );

              return (
                <ClickableTableRow key={facility.id} clickable={clickable} onClick={handleClick}>
                  <TableCell>{facility.name}</TableCell>
                  <TableCell>{facility.rmvId}</TableCell>
                  <TableCell>{facility.publication ? 'Öffentlich' : 'Versteckt'}</TableCell>
                  <TableCell>
                    {facility.updatedAt ? dayjs(facility.updatedAt).format('DD.MM.YYYY HH:mm') : '-'}
                  </TableCell>
                  <TableCell>{facility.organization?.name ?? '-'}</TableCell>
                </ClickableTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={facilities.length}
        labelRowsPerPage="Zeige"
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Stack>
  );
}
